import {
  createCoursePathObjective,
  updateCoursePathObjective,
  deleteCoursePathObjective,
} from '@/data/services/coursepathObjectiveServices';
import { TempObjective } from '@/models/CoursePath';
import { buildChangedObject } from '@/utils/buildChangedObject';
import { separeArrayStudioActions } from '../info/CourseStudioInfo.hooks';

async function updateObjective(
  slug: string,
  currentObjective: TempObjective,
  newObjective: TempObjective,
) {
  const changes = buildChangedObject(currentObjective, newObjective, {
    ignoreFields: ['tempId'],
  });

  if (Object.keys(changes).length === 0) return currentObjective;

  return await updateCoursePathObjective(slug, newObjective.id, changes);
}

async function performDeleteObjectives(
  slug: string,
  objectives: TempObjective[],
): Promise<TempObjective[]> {
  const deletedObjectives: TempObjective[] = [];

  for (const objective of objectives) {
    if (objective.id) {
      await deleteCoursePathObjective(slug, objective.id);
      deletedObjectives.push(objective);
    }
  }

  return deletedObjectives;
}

async function performUpdateObjective(
  slug: string,
  currentObjectives: TempObjective[],
  objectivesToUpdate: TempObjective[],
) {
  const updatedObjectives: TempObjective[] = [];

  for (const objective of objectivesToUpdate) {
    const currentObjective = currentObjectives.find(o => o.id === objective.id);
    if (!currentObjective) continue;

    const updated = await updateObjective(slug, currentObjective, objective);
    updatedObjectives.push(updated);
  }

  return updatedObjectives;
}

async function performCreateObjectives(
  slug: string,
  objectives: TempObjective[],
) {
  const updatedObjectives: TempObjective[] = [];

  for (const objective of objectives) {
    const created = await createCoursePathObjective(slug, {
      name: objective.name,
      description: objective.description,
    });
    updatedObjectives.push(created);
  }

  return updatedObjectives;
}

export function useCourseStudioObjectives() {
  async function handleCreateObjectives(
    coursePathSlug: string,
    objectives: TempObjective[],
  ): Promise<TempObjective[]> {
    const createdObjectives: TempObjective[] = [];

    for (const objective of objectives) {
      if (!objective.name) continue;
      const created = await createCoursePathObjective(
        coursePathSlug,
        objective,
      );
      createdObjectives.push(created);
    }

    return createdObjectives;
  }

  async function handleUpdateObjectives(
    slug: string,
    currentObjectives: TempObjective[],
    newObjectives: TempObjective[],
  ) {
    const { arrayToDelete, arrayToUpdate, arrayToCreate } =
      separeArrayStudioActions(currentObjectives, newObjectives);

    await performDeleteObjectives(slug, arrayToDelete);

    const updatedObjectivesFromUpdate = await performUpdateObjective(
      slug,
      currentObjectives,
      arrayToUpdate,
    );
    const updatedObjectivesFromCreate = await performCreateObjectives(
      slug,
      arrayToCreate,
    );

    const updatedObjectives = [
      ...updatedObjectivesFromUpdate,
      ...updatedObjectivesFromCreate,
    ];

    return updatedObjectives;
  }

  return {
    handleCreateObjectives,
    handleUpdateObjectives,
  };
}
